import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

/**
 * Ads / attribution context.
 * - Captures UTM, gclid, fbclid, promo from URL on first render.
 * - Persists to localStorage (last-touch attribution) for cart + checkout tracking.
 * - Posts a visit to /api/ads/track when new ad params are seen.
 * - Exposes `adMode` flag (any UTM or click id present) so the UI can suppress
 *   distractions (age gate defaults to accepted, popups muted, sticky offer shown).
 */
const KEY = "bb_attribution_v1";
const AD_MODE_KEY = "bb_ad_mode_v1";
const PROMO_KEY = "bb_ad_promo_v1";
const ATTR_FIELDS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "gclid", "fbclid"];

const AdsContext = createContext(null);

function parseCurrent() {
  if (typeof window === "undefined") return { attribution: null, promo: null };
  const usp = new URLSearchParams(window.location.search);
  const attr = {};
  let seen = false;
  for (const f of ATTR_FIELDS) {
    const v = usp.get(f);
    if (v) { attr[f] = v; seen = true; }
  }
  if (seen) {
    attr.landing_path = window.location.pathname;
    attr.referrer = document.referrer || null;
    attr.first_seen_at = new Date().toISOString();
  }
  const promo = usp.get("promo") || usp.get("code") || null;
  return { attribution: seen ? attr : null, promo };
}

export function AdsProvider({ children }) {
  const [attribution, setAttribution] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; }
  });
  const [adMode, setAdMode] = useState(() => localStorage.getItem(AD_MODE_KEY) === "1");
  const [promoCode, setPromoCode] = useState(() => localStorage.getItem(PROMO_KEY) || null);

  useEffect(() => {
    const { attribution: fresh, promo } = parseCurrent();
    if (fresh) {
      localStorage.setItem(KEY, JSON.stringify(fresh));
      localStorage.setItem(AD_MODE_KEY, "1");
      setAttribution(fresh);
      setAdMode(true);
      // Fire visit event (non-blocking)
      api.post("/ads/track", {
        landing_path: fresh.landing_path,
        utm_source: fresh.utm_source, utm_medium: fresh.utm_medium,
        utm_campaign: fresh.utm_campaign, utm_content: fresh.utm_content,
        utm_term: fresh.utm_term,
        gclid: fresh.gclid, fbclid: fresh.fbclid,
        referrer: fresh.referrer,
        cart_id: localStorage.getItem("bb_cart_id_v1"),
        hero_variant: localStorage.getItem("bb_hero_shown"),
        detected_city: localStorage.getItem("bb_detected_city"),
        detected_region: localStorage.getItem("bb_detected_region"),
      }, { withCredentials: true }).catch((err) => {
        console.error("Ad tracking beacon failed:", err);
      });
    }
    if (promo) {
      localStorage.setItem(PROMO_KEY, promo.toUpperCase());
      setPromoCode(promo.toUpperCase());
    }
    // Also arm ad mode if the incoming URL is a paid landing (geo route)
    // — this is the "point paid Google/Meta traffic at city URLs" behaviour.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearPromo = () => {
    localStorage.removeItem(PROMO_KEY);
    setPromoCode(null);
  };

  const value = useMemo(
    () => ({ attribution, adMode, promoCode, setPromoCode, clearPromo }),
    [attribution, adMode, promoCode]
  );

  return <AdsContext.Provider value={value}>{children}</AdsContext.Provider>;
}

export const useAds = () => useContext(AdsContext);
